/**
 * 재고 · 물류 시안의 가짜 데이터 (2026-10-06). 저장 안 됨 — 화면 느낌만 본다.
 * 실제 꼴은 hcltech-parts `docs/INVENTORY_UNIT_DESIGN.md` · `STATUS_TRANSITIONS.md` 를 따른다.
 */

export type StockStatus = 'inspecting' | 'available' | 'reserved' | 'dispatched' | 'retired';
export type Origin = 'purchased' | 'legacy' | 'disassembled' | 'assembled' | 'found' | 'customer';

export interface PreviewUnit {
  id: number;
  option_part: string;
  description: string;
  category: string;
  condition: string;            // 신품 · 중고 A · 중고 B · scrap
  warehouse: string;
  zone: string | null;
  serial: string | null;
  status: StockStatus;
  origin: Origin;
  parent_unit_id: number | null;
  cost_amount: number | null;
  cost_currency: string;
  quote_ref: string | null;     // 묶인 견적 줄 — 'Q-2026-0142 #3'
}

export interface PreviewEvent {
  at: string;
  unit_id: number;
  option_part: string;
  serial: string | null;
  type: '입고' | '검수' | '이동' | '세팅' | '출고' | '반품' | '이탈' | '고침';
  detail: string;
  by: string;
  reason: string;
}

export interface PreviewRequest {
  id: number;
  kind: '세팅' | '출고' | '직송';
  quote: string;
  customer: string;
  items: string;
  qty: number;
  requested_by: string;
  requested_at: string;
  note: string;
  status: '대기' | '진행' | '끝';
}

export interface PreviewPoLine {
  po: string;
  supplier: string;
  option_part: string;
  description: string;
  qty: number;
  received: number;
  unit_cost: number;
  currency: string;
  is_server: boolean;
  parts: { option_part: string; description: string; ref_cost: number | null; qty: number }[];   // 서버 한 대 안의 부품 — 원가 배분 재료
}

export interface PreviewSoLine {
  so: string;
  customer: string;
  quote: string;
  option_part: string;
  description: string;
  qty: number;
  dispatched: number;
  reserved_serials: string[];
  is_drop_ship: boolean;
}

export interface PreviewSetupNote { option_part: string; note: string; by: string; at: string; where: string }

export interface PreviewZone { warehouse: string; zone: string; category: string | null; note: string }

const UNITS: PreviewUnit[] = [
  { id: 10231, option_part: 'P19766-B21', description: 'HPE ProLiant DL380 Gen10 8SFF CTO Server', category: '섀시', condition: '중고 A', warehouse: '본사', zone: 'S-1', serial: 'CZ20350K7X', status: 'available', origin: 'purchased', parent_unit_id: null, cost_amount: 1850000, cost_currency: 'KRW', quote_ref: null },
  { id: 10232, option_part: 'P24487-B21', description: 'Intel Xeon-Gold 6248R (3.0GHz/24-core/205W)', category: 'CPU', condition: '중고 A', warehouse: '본사', zone: 'S-1', serial: null, status: 'available', origin: 'purchased', parent_unit_id: 10231, cost_amount: 420000, cost_currency: 'KRW', quote_ref: null },
  { id: 10233, option_part: 'P24487-B21', description: 'Intel Xeon-Gold 6248R (3.0GHz/24-core/205W)', category: 'CPU', condition: '중고 A', warehouse: '본사', zone: 'S-1', serial: null, status: 'available', origin: 'purchased', parent_unit_id: 10231, cost_amount: 420000, cost_currency: 'KRW', quote_ref: null },
  { id: 10234, option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', category: '메모리', condition: '중고 A', warehouse: '본사', zone: 'S-1', serial: '80AD012052', status: 'available', origin: 'purchased', parent_unit_id: 10231, cost_amount: 95000, cost_currency: 'KRW', quote_ref: null },
  { id: 10240, option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', category: '메모리', condition: '중고 A', warehouse: '본사', zone: 'M-2', serial: '80AD012077', status: 'available', origin: 'legacy', parent_unit_id: null, cost_amount: 95000, cost_currency: 'KRW', quote_ref: null },
  { id: 10241, option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', category: '메모리', condition: '중고 A', warehouse: '본사', zone: 'M-2', serial: '80AD012078', status: 'reserved', origin: 'legacy', parent_unit_id: null, cost_amount: 95000, cost_currency: 'KRW', quote_ref: 'Q-2026-0142 #3' },
  { id: 10242, option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', category: '메모리', condition: '중고 B', warehouse: '본사', zone: 'M-2', serial: null, status: 'inspecting', origin: 'purchased', parent_unit_id: null, cost_amount: 70000, cost_currency: 'KRW', quote_ref: null },
  { id: 10243, option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', category: '메모리', condition: '신품', warehouse: '본사', zone: 'M-1', serial: '80AD020111', status: 'available', origin: 'purchased', parent_unit_id: null, cost_amount: 140000, cost_currency: 'KRW', quote_ref: null },
  { id: 10250, option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', category: 'SSD', condition: '신품', warehouse: '본사', zone: 'D-1', serial: 'S5R2NE0R123', status: 'available', origin: 'purchased', parent_unit_id: null, cost_amount: 210000, cost_currency: 'KRW', quote_ref: null },
  { id: 10251, option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', category: 'SSD', condition: '신품', warehouse: '본사', zone: 'D-1', serial: 'S5R2NE0R124', status: 'dispatched', origin: 'purchased', parent_unit_id: null, cost_amount: 210000, cost_currency: 'KRW', quote_ref: 'Q-2026-0131 #1' },
  { id: 10252, option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', category: 'SSD', condition: 'scrap', warehouse: '본사', zone: 'X-1', serial: 'S5R2NE0R099', status: 'available', origin: 'legacy', parent_unit_id: null, cost_amount: 0, cost_currency: 'KRW', quote_ref: null },
  { id: 10260, option_part: 'P26262-B21', description: 'HPE DL38X Gen10 Plus 2U 8SFF CTO Server', category: '섀시', condition: '중고 A', warehouse: '지사', zone: null, serial: 'CZ21240ABC', status: 'available', origin: 'customer', parent_unit_id: null, cost_amount: null, cost_currency: 'KRW', quote_ref: null },
  { id: 10261, option_part: 'P36997-B21', description: 'HPE Smart Array P408i-a SR Gen10 Controller', category: '컨트롤러', condition: '중고 A', warehouse: '본사', zone: 'C-1', serial: 'PEYHB0ARCCK', status: 'retired', origin: 'legacy', parent_unit_id: null, cost_amount: 60000, cost_currency: 'KRW', quote_ref: null },
];

const EVENTS: PreviewEvent[] = [
  { at: '2026-10-06 09:12', unit_id: 10242, option_part: 'P00924-B21', serial: null, type: '입고', detail: 'PO-2026-088 줄 2 · 검수 중', by: '박제품', reason: '' },
  { at: '2026-10-05 17:40', unit_id: 10241, option_part: 'P00924-B21', serial: '80AD012078', type: '세팅', detail: 'Q-2026-0142 #3 에 묶임 (available → reserved)', by: '박제품', reason: '' },
  { at: '2026-10-05 15:02', unit_id: 10251, option_part: 'P18434-B21', serial: 'S5R2NE0R124', type: '출고', detail: 'Q-2026-0131 #1 · 고객 ㈜한빛 (reserved → dispatched)', by: '이출고', reason: '' },
  { at: '2026-10-04 11:30', unit_id: 10252, option_part: 'P18434-B21', serial: 'S5R2NE0R099', type: '고침', detail: '컨디션 중고 B → scrap', by: '박제품', reason: 'regraded · SMART 오류' },
  { at: '2026-10-03 10:10', unit_id: 10261, option_part: 'P36997-B21', serial: 'PEYHB0ARCCK', type: '이탈', detail: 'available → retired', by: '박제품', reason: 'damaged · 캐시 모듈 불량' },
  { at: '2026-10-02 14:00', unit_id: 10240, option_part: 'P00924-B21', serial: '80AD012077', type: '이동', detail: '본사 M-1 → M-2', by: '김창고', reason: '' },
  { at: '2026-10-02 09:00', unit_id: 10231, option_part: 'P19766-B21', serial: 'CZ20350K7X', type: '검수', detail: 'inspecting → available · 자식 3 같이', by: '박제품', reason: '' },
  { at: '2026-10-01 16:20', unit_id: 10231, option_part: 'P19766-B21', serial: 'CZ20350K7X', type: '입고', detail: 'PO-2026-081 줄 1 · 서버 한 대(자식 3) · 원가 배분', by: '박제품', reason: '' },
  { at: '2026-09-30 13:05', unit_id: 10250, option_part: 'P18434-B21', serial: 'S5R2NE0R123', type: '반품', detail: 'Q-2026-0120 #2 · dispatched → inspecting', by: '이출고', reason: '' },
];

const REQUESTS: PreviewRequest[] = [
  { id: 71, kind: '세팅', quote: 'Q-2026-0142', customer: '㈜대한전산', items: 'DL380 Gen10 · 6248R ×2 · 32GB ×12 · 960GB ×4', qty: 1, requested_by: '최영업', requested_at: '2026-10-06 08:50', note: '10/8 납품. iLO Advanced 키 포함', status: '대기' },
  { id: 70, kind: '출고', quote: 'Q-2026-0139', customer: '㈜한빛', items: 'P00924-B21 32GB', qty: 4, requested_by: '최영업', requested_at: '2026-10-05 18:10', note: '퀵 착불', status: '진행' },
  { id: 69, kind: '직송', quote: 'Q-2026-0137', customer: '서울대병원', items: 'P18434-B21 960GB SSD', qty: 2, requested_by: '정영업', requested_at: '2026-10-05 11:00', note: '업체 → 고객 직송. PO-2026-090', status: '대기' },
  { id: 66, kind: '세팅', quote: 'Q-2026-0131', customer: '㈜한빛', items: 'DL360 Gen10 · 4214R ×1 · 16GB ×4', qty: 1, requested_by: '최영업', requested_at: '2026-10-03 09:30', note: '', status: '끝' },
];

const PO_LINES: PreviewPoLine[] = [
  { po: 'PO-2026-090', supplier: '유니온테크', option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', qty: 2, received: 0, unit_cost: 190000, currency: 'KRW', is_server: false, parts: [] },
  { po: 'PO-2026-091', supplier: 'ServerSupply', option_part: 'P19766-B21', description: 'HPE DL380 Gen10 8SFF (6248R ×2 · 32GB ×8 · P408i-a · 800W ×2)', qty: 1, received: 0, unit_cost: 3200, currency: 'USD', is_server: true, parts: [
    { option_part: 'P19766-B21', description: '섀시 DL380 Gen10 8SFF', ref_cost: 900, qty: 1 },
    { option_part: 'P24487-B21', description: 'Xeon-Gold 6248R', ref_cost: 350, qty: 2 },
    { option_part: 'P00924-B21', description: '32GB 2Rx4 PC4-2933Y-R', ref_cost: 80, qty: 8 },
    { option_part: 'P36997-B21', description: 'P408i-a 컨트롤러', ref_cost: 60, qty: 1 },
    { option_part: 'P38995-B21', description: '800W Flex Slot PSU', ref_cost: null, qty: 2 },
  ] },
  { po: 'PO-2026-088', supplier: '유니온테크', option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', qty: 6, received: 1, unit_cost: 70000, currency: 'KRW', is_server: false, parts: [] },
];

const SO_LINES: PreviewSoLine[] = [
  { so: 'SO-2026-0142', customer: '㈜대한전산', quote: 'Q-2026-0142', option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', qty: 2, dispatched: 0, reserved_serials: ['80AD012078', '80AD012077'], is_drop_ship: false },
  { so: 'SO-2026-0139', customer: '㈜한빛', quote: 'Q-2026-0139', option_part: 'P00924-B21', description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', qty: 4, dispatched: 2, reserved_serials: ['80AD020111', '80AD020112'], is_drop_ship: false },
  { so: 'SO-2026-0137', customer: '서울대병원', quote: 'Q-2026-0137', option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', qty: 2, dispatched: 0, reserved_serials: [], is_drop_ship: true },
  { so: 'SO-2026-0131', customer: '㈜한빛', quote: 'Q-2026-0131', option_part: 'P18434-B21', description: 'HPE 960GB SATA RI SFF SC MV SSD', qty: 1, dispatched: 1, reserved_serials: ['S5R2NE0R124'], is_drop_ship: false },
];

const SETUP_NOTES: PreviewSetupNote[] = [
  { option_part: 'P00924-B21', note: 'Gen10 에 2933 꽂으면 2666 으로 내려감 — 고객에게 미리 말할 것', by: '박제품', at: '2026-09-28', where: 'HCL-SV-000042' },
  { option_part: 'P00924-B21', note: '라벨 (P) 번호가 PN 과 다름 — 어셈블리 번호로 검색', by: '박제품', at: '2026-09-12', where: '' },
  { option_part: 'P19766-B21', note: '2U 레일킷 동봉 안 된 채 오는 경우 있음(PO 줄에 레일 따로)', by: '이출고', at: '2026-08-30', where: 'HCL-SV-000039' },
];

const ZONES: PreviewZone[] = [
  { warehouse: '본사', zone: 'S-1', category: '섀시', note: '서버 랙 1' },
  { warehouse: '본사', zone: 'M-1', category: '메모리', note: '신품 서랍' },
  { warehouse: '본사', zone: 'M-2', category: '메모리', note: '중고 서랍' },
  { warehouse: '본사', zone: 'D-1', category: 'SSD', note: '' },
  { warehouse: '본사', zone: 'C-1', category: '컨트롤러', note: '' },
  { warehouse: '본사', zone: 'X-1', category: null, note: '불용 · 적출 대기' },
  { warehouse: '지사', zone: null as unknown as string, category: null, note: '구역 없음' },
];

export const PREVIEW = { UNITS, EVENTS, REQUESTS, PO_LINES, SO_LINES, SETUP_NOTES, ZONES };

export const STATUS_WORD: Record<StockStatus, string> = { inspecting: '검수 중', available: '가용', reserved: '판매 예정', dispatched: '출고됨', retired: '이탈' };
export const ORIGIN_WORD: Record<Origin, string> = { purchased: '구매', legacy: '옛 재고', disassembled: '분해', assembled: '조립', found: '실사 발견', customer: '고객 장비' };
